import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const SKELETON_ROWS = ["sk-0", "sk-1", "sk-2"] as const;

// Shared loading / error / empty scaffolding for dialogs that load data via
// useQuery on open. Callers gate their own success render — this component
// returns `null` once data is loaded so the dialog can paint its body.
//
// Usage:
//   <DialogQueryState
//     isLoading={q.isLoading}
//     isError={q.isError}
//     error={q.error}
//     onRetry={() => q.refetch()}
//     isEmpty={!q.data || q.data.length === 0}
//     emptyIcon={FileText}
//     emptyMessage="No related policies found."
//   />
//   {q.data && q.data.length > 0 && (...success render...)}
export function DialogQueryState({
  isLoading,
  isError,
  error,
  onRetry,
  errorMessage = "Failed to load.",
  isEmpty,
  emptyIcon: EmptyIcon,
  emptyMessage,
  skeletonHeight = "h-12",
}: {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  errorMessage?: string;
  isEmpty?: boolean;
  emptyIcon?: LucideIcon;
  emptyMessage?: string;
  skeletonHeight?: string;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {SKELETON_ROWS.map((key) => (
          <div key={key} className={`${skeletonHeight} animate-pulse rounded bg-muted`} />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="py-8 text-center">
        <p className="text-destructive text-sm">
          {error instanceof Error ? error.message : errorMessage}
        </p>
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-3">
          Try again
        </Button>
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className="py-8 text-center">
        {EmptyIcon && <EmptyIcon className="mx-auto h-10 w-10 text-muted-foreground/40" />}
        {emptyMessage && <p className="mt-2 text-muted-foreground text-sm">{emptyMessage}</p>}
      </div>
    );
  }
  return null;
}
